import { z } from 'zod';

import type { ActionEntity, ActionOperation, ResolvableCrmEntity } from '../action-spec/types.js';
import type { BulkFilterAst } from '../bulk-action/types.js';

const actionEntities = [
  'deal',
  'task',
  'note',
  'contact',
  'company',
  'calendar_event',
  'contact_interaction',
  'document',
  'template',
  'attachment',
] as const satisfies readonly ActionEntity[];

const actionOperations = [
  'read',
  'create',
  'update',
  'soft_delete',
  'stage',
  'status',
  'cancel',
  'activate',
  'deactivate',
  'link',
  'unlink',
  'add_note',
  'owner',
  'assignee',
  'tags',
  'due_date',
  'participants',
  'transitions',
  'interactions',
] as const satisfies readonly ActionOperation[];

export const plannerActionEntitySchema = z.enum(actionEntities);
export const plannerRelationEntitySchema: z.ZodType<ResolvableCrmEntity> = z.enum([
  ...actionEntities,
  'workspace_member',
] as const);
export const plannerActionOperationSchema = z.enum(['search', ...actionOperations]);
export const intentModeSchema = z.enum([
  'conversation',
  'crm_read',
  'crm_write',
  'special',
  'unsupported',
]);
export const intentConfidenceSchema = z.enum(['high', 'medium', 'low']);

const sourceTextSchema = z.string().trim().min(1).max(2_000);
const sourceTextItemsSchema = z.array(sourceTextSchema).min(1).max(50);
const clientOperationIdSchema = z.string().trim().min(1).max(80);

function hasDependencyCycle(
  operations: readonly {
    readonly clientOperationId: string;
    readonly dependsOn: readonly string[];
  }[],
): boolean {
  const dependencies = new Map(
    operations.map((operation) => [operation.clientOperationId, operation.dependsOn]),
  );
  const visiting = new Set<string>();
  const visited = new Set<string>();

  const visit = (operationId: string): boolean => {
    if (visiting.has(operationId)) return true;
    if (visited.has(operationId)) return false;
    visiting.add(operationId);
    for (const dependency of dependencies.get(operationId) ?? []) {
      if (dependencies.has(dependency) && visit(dependency)) return true;
    }
    visiting.delete(operationId);
    visited.add(operationId);
    return false;
  };

  return operations.some((operation) => visit(operation.clientOperationId));
}

export const sourceFactSchema = z
  .object({
    field: z.string().trim().min(1).max(120),
    rawValue: sourceTextSchema,
    sourceQuote: sourceTextSchema,
    /**
     * Server-attached owner when the fact is attributed to one operation.
     * The model never sends it; verification reports carry it so a missing
     * or changed fact names its clientOperationId.
     */
    operationId: clientOperationIdSchema.optional(),
  })
  .strict();

/**
 * A field may be a scalar literal or a list of separately source-proven text
 * literals. The latter lets the planner express tags/participants without
 * smuggling a phrase-regex interpretation into execution.
 */
export const sourceFieldSchema = z
  .object({
    field: z.string().trim().min(1).max(120),
    rawValue: z.union([sourceTextSchema, sourceTextItemsSchema]),
    sourceQuote: sourceTextSchema,
    normalizedValue: z
      .union([
        z.string().trim().min(1).max(2_000),
        z.number().finite(),
        z.boolean(),
        sourceTextItemsSchema,
      ])
      .optional(),
  })
  .strict();

export const userQuotedEntityReferenceDraftSchema = z
  .object({
    entity: plannerActionEntitySchema,
    source: z.literal('user_quoted').optional(),
    displayName: sourceTextSchema,
    sourceQuote: sourceTextSchema,
  })
  .strict();

export const trustedContextEntityReferenceDraftSchema = z
  .object({
    entity: plannerActionEntitySchema,
    source: z.literal('trusted_context'),
  })
  .strict();

/**
 * A trusted-context reference carries no model-authored label or identifier.
 * It can only be bound later to the single scope-verified entity already
 * attached to the turn. User-quoted references retain their original strict
 * source proof.
 */
export const entityReferenceDraftSchema = z.union([
  userQuotedEntityReferenceDraftSchema,
  trustedContextEntityReferenceDraftSchema,
]);

/** Relation references additionally allow an active workspace member. */
export const userQuotedRelationEntityReferenceDraftSchema = z
  .object({
    entity: plannerRelationEntitySchema,
    source: z.literal('user_quoted').optional(),
    displayName: sourceTextSchema,
    sourceQuote: sourceTextSchema,
  })
  .strict();

/**
 * Server-attested relation provenance. The trusted form carries the entity
 * type and nothing else: no model-authored id, no model-authored label. It is
 * bound server-side to the single scope-verified context entity of that kind
 * (re-read through the scope-first adapter), which is how an anaphoric relation
 * a user cannot quote by name ("этого контакта" from a contact card) is
 * expressed. The relation-level sourceQuote stays mandatory and must still be
 * covered by the user text, so the reference itself remains proven.
 */
export const trustedContextRelationEntityReferenceDraftSchema = z
  .object({
    entity: plannerRelationEntitySchema,
    source: z.literal('trusted_context'),
  })
  .strict();

export const relationEntityReferenceDraftSchema = z.union([
  userQuotedRelationEntityReferenceDraftSchema,
  trustedContextRelationEntityReferenceDraftSchema,
]);

export const relationDraftSchema = z
  .object({
    field: z.string().trim().min(1).max(120),
    target: relationEntityReferenceDraftSchema,
    sourceQuote: sourceTextSchema,
  })
  .strict();

export const missingInformationSchema = z
  .object({
    field: z.string().trim().min(1).max(120),
    reason: sourceTextSchema,
  })
  .strict();

/**
 * Bulk mass-target selection draft. For mass phrases ("поставь всем задачам
 * статус done") the model describes "which many" structurally and the server
 * alone resolves the filter into exact IDs, so identifiers never appear in
 * the envelope. The recursive filter mirrors BulkFilterAst from
 * ../bulk-action/types.ts: predicates on allowlisted fields combined through
 * and/or groups.
 */
const jsonPrimitiveSchema = z.union([z.string(), z.number(), z.boolean(), z.null()]);

const bulkFilterPredicateSchema = z
  .object({
    kind: z.literal('predicate'),
    field: z.string().trim().min(1).max(120),
    operator: z.enum(['eq', 'in', 'contains', 'before', 'after', 'is_null']),
    value: z.union([jsonPrimitiveSchema, z.array(jsonPrimitiveSchema)]).optional(),
  })
  .strict();

const bulkFilterGroupSchema = z
  .object({
    kind: z.enum(['and', 'or']),
    nodes: z.array(z.lazy(() => bulkFilterAstSchema)),
  })
  .strict();

// Design fallback (accepted): zod's ZodOptional infers `value?: T | undefined`,
// which exactOptionalPropertyTypes will not assign to BulkFilterPredicate's
// `value?: T`. The runtime schema is structurally exact for BulkFilterAst, so
// the recursive root carries a single documented cast to z.ZodType<BulkFilterAst>.
//
// Edge tolerance: weak planners often emit a predicate without the `kind`
// discriminator ({"field":"status","operator":"eq"}). The shape is unambiguous
// (field+operator exist, no group keys), so the boundary normalizes it to a
// canonical predicate; groups and malformed nodes still fail closed. The
// server-side parseBulkFilterAst only ever sees the canonical form.
const bulkFilterAstSchema = z.preprocess(
  (value) => {
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      const record = value as Readonly<Record<string, unknown>>;
      if (!('kind' in record) && 'field' in record && 'operator' in record) {
        return { ...record, kind: 'predicate' } as unknown;
      }
    }
    return value;
  },
  z.union([bulkFilterPredicateSchema, bulkFilterGroupSchema]),
) as unknown as z.ZodType<BulkFilterAst>;

/**
 * Server-owned bulk selection: operation maps onto the envelope operation
 * literal (bulk_update↔update-family, bulk_delete↔soft_delete,
 * bulk_create↔create), filter selects the target set, and patch carries the
 * shared update payload — for bulk_create the per-item payload array.
 */
const selectionSchema = z
  .object({
    operation: z.enum(['bulk_update', 'bulk_delete', 'bulk_create']),
    filter: bulkFilterAstSchema.optional(),
    patch: z.unknown(),
  })
  .strict();

/**
 * Envelope operation literals mapping to selection.operation bulk_update.
 * Typed ReadonlySet<string> because planner operations include 'search',
 * which is deliberately absent from the ActionOperation family.
 */
const bulkUpdateOperations: ReadonlySet<string> = new Set<ActionOperation>([
  'update',
  'status',
  'assignee',
  'due_date',
  'stage',
  'owner',
  'tags',
  'link',
  'unlink',
  'cancel',
  'participants',
  'transitions',
  'activate',
  'deactivate',
]);

// Mirror BULK_SNAPSHOT_LIMITS (maxFilterDepth/maxFilterNodes) from
// ../bulk-action/types.ts: an oversized draft fails closed at the envelope
// boundary instead of reaching selection compilation.
const bulkFilterMaxDepth = 4;
const bulkFilterMaxNodes = 32;
// Mirror BULK_SNAPSHOT_LIMITS.maxItems for the explicit bulk_create item list.
const bulkCreateMaxItems = 100;

function findBulkFilterLimitViolation(node: BulkFilterAst): 'depth' | 'nodes' | undefined {
  let nodeCount = 0;
  const visit = (current: BulkFilterAst, depth: number): 'depth' | 'nodes' | undefined => {
    nodeCount += 1;
    if (nodeCount > bulkFilterMaxNodes) return 'nodes';
    if (current.kind === 'predicate') {
      return depth > bulkFilterMaxDepth ? 'depth' : undefined;
    }
    if (depth > bulkFilterMaxDepth) return 'depth';
    for (const child of current.nodes) {
      const violation = visit(child, depth + 1);
      if (violation !== undefined) return violation;
    }
    return undefined;
  };
  return visit(node, 1);
}

export const intentOperationSchema = z
  .object({
    clientOperationId: clientOperationIdSchema,
    entity: plannerActionEntitySchema,
    operation: plannerActionOperationSchema,
    target: entityReferenceDraftSchema.nullable(),
    fields: z.array(sourceFieldSchema).max(50),
    relations: z.array(relationDraftSchema).max(30),
    dependsOn: z.array(clientOperationIdSchema).max(20),
    /**
     * Optional server-owned bulk selection for mass phrases. Mutually
     * exclusive with target: the server resolves the filter into exact IDs,
     * so the model never lists identifiers. patch carries the shared payload
     * (bulk_create: the per-item payload array).
     */
    selection: selectionSchema.optional(),
    /**
     * Verifiable operation provenance: the exact user phrase authorizing
     * THIS operation, starting at its command verb, copied verbatim from
     * the user message. The server checks that the quote really occurs in
     * the source message exactly once and that every field sourceQuote of
     * this operation sits inside the quoted span, so a name from another
     * operation (deal/task/note/company title) can never prove this one.
     * Reads may omit it; contact creates must carry it.
     */
    sourceQuote: sourceTextSchema.optional(),
    /**
     * Optional span hint. When either bound is present, both must be present
     * and must equal exactly the [start, end) occurrence of sourceQuote in
     * the source message; a partial or mismatched hint fails verification.
     * The server always re-derives the span from the verified quote
     * occurrence and never trusts these numbers on their own.
     */
    sourceStart: z.number().int().min(0).max(100_000).optional(),
    sourceEnd: z.number().int().min(0).max(100_000).optional(),
  })
  .strict()
  .superRefine((operation, context) => {
    if (operation.dependsOn.includes(operation.clientOperationId)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Operation cannot depend on itself',
      });
    }
    if (
      operation.sourceStart !== undefined &&
      operation.sourceEnd !== undefined &&
      operation.sourceEnd <= operation.sourceStart
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Operation sourceEnd must exceed sourceStart',
      });
    }
    if (new Set(operation.dependsOn).size !== operation.dependsOn.length) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Operation dependencies must be unique',
      });
    }
    if (operation.selection !== undefined) {
      const selection = operation.selection;
      if (operation.operation === 'read' || operation.operation === 'search') {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['selection'],
          message: 'selection is valid only for mutation operations',
        });
      }
      if (operation.target !== null) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['selection'],
          message: 'selection and target are mutually exclusive: target must be null',
        });
      }
      const operationLiteralAllowed =
        selection.operation === 'bulk_update'
          ? bulkUpdateOperations.has(operation.operation)
          : selection.operation === 'bulk_delete'
            ? operation.operation === 'soft_delete'
            : operation.operation === 'create';
      if (!operationLiteralAllowed) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['selection', 'operation'],
          message: `selection ${selection.operation} does not match operation ${operation.operation}`,
        });
      }
      if (selection.patch === undefined) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['selection', 'patch'],
          message: 'selection patch is required',
        });
      }
      if (selection.operation === 'bulk_create') {
        if (selection.filter !== undefined) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['selection', 'filter'],
            message: 'bulk_create selection forbids filter: patch carries per-item payloads',
          });
        }
        if (selection.patch !== undefined && !Array.isArray(selection.patch)) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['selection', 'patch'],
            message: 'bulk_create patch must be an array of per-item payloads',
          });
        }
        // The model lists create items explicitly, so the bulk bound is
        // checkable at the envelope boundary: fewer than two is not a bulk
        // operation, more than the snapshot limit fails closed here instead
        // of reaching selection compilation (mirrors BULK_SNAPSHOT_LIMITS).
        if (Array.isArray(selection.patch)) {
          if (selection.patch.length < 2) {
            context.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['selection', 'patch'],
              message: 'bulk_create selection needs at least two per-item payloads',
            });
          }
          if (selection.patch.length > bulkCreateMaxItems) {
            context.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['selection', 'patch'],
              message: 'bulk_create selection exceeds the per-item bound',
            });
          }
        }
      } else if (selection.filter === undefined) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['selection', 'filter'],
          message: `${selection.operation} selection requires a filter`,
        });
      }
    }
  });

export const intentEnvelopeV1Schema = z
  .object({
    version: z.literal(1),
    mode: intentModeSchema,
    operations: z.array(intentOperationSchema).max(20),
    specialCapabilityId: z.string().trim().min(1).max(120).optional(),
    /** A user-quoted target required by a metadata-scoped special capability. */
    specialTarget: z
      .union([
        entityReferenceDraftSchema,
        userQuotedEntityReferenceDraftSchema.extend({ entity: z.literal('project') }),
      ])
      .nullable()
      .optional(),
    answerRequest: z.string().trim().min(1).max(2_000).optional(),
    missingInformation: z.array(missingInformationSchema).max(30),
    unsupportedRequirements: z.array(sourceFactSchema).max(30),
    confidence: intentConfidenceSchema,
  })
  .strict()
  .superRefine((envelope, context) => {
    const readOperations = new Set(['search', 'read']);
    const mutationCount = envelope.operations.filter(
      (operation) => !readOperations.has(operation.operation),
    ).length;
    if (
      (envelope.mode === 'conversation' || envelope.mode === 'unsupported') &&
      envelope.operations.length > 0
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['operations'],
        message: `${envelope.mode} intents cannot contain CRM operations`,
      });
    }
    if (envelope.mode === 'crm_read' && envelope.operations.length === 0) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['operations'],
        message: 'crm_read intents require at least one operation',
      });
    }
    if (
      envelope.mode === 'crm_read' &&
      envelope.operations.some((operation) => !readOperations.has(operation.operation))
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['operations'],
        message: 'crm_read intents can contain only search/read operations',
      });
    }
    if (envelope.mode === 'crm_write' && mutationCount === 0) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['operations'],
        message: 'crm_write intents require at least one mutation operation',
      });
    }
    if (
      envelope.mode === 'crm_write' &&
      envelope.operations.some((operation) => readOperations.has(operation.operation))
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['operations'],
        message: 'crm_write intents can contain only mutation operations',
      });
    }
    if (envelope.mode === 'special' && envelope.specialCapabilityId === undefined) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['specialCapabilityId'],
        message: 'special intents require specialCapabilityId',
      });
    }
    if (envelope.mode === 'special' && envelope.operations.length > 0) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['operations'],
        message: 'special intents cannot contain generic CRM operations',
      });
    }
    if (envelope.mode !== 'special' && envelope.specialCapabilityId !== undefined) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['specialCapabilityId'],
        message: 'specialCapabilityId is valid only for special intents',
      });
    }
    if (envelope.mode !== 'special' && envelope.specialTarget !== undefined) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['specialTarget'],
        message: 'specialTarget is valid only for special intents',
      });
    }
    if (envelope.mode === 'unsupported' && envelope.unsupportedRequirements.length === 0) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['unsupportedRequirements'],
        message: 'unsupported intents require at least one unsupported requirement',
      });
    }
    const operationIds = envelope.operations.map((operation) => operation.clientOperationId);
    if (new Set(operationIds).size !== operationIds.length) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['operations'],
        message: 'Operation IDs must be unique',
      });
    }
    const knownOperationIds = new Set(operationIds);
    for (const [index, operation] of envelope.operations.entries()) {
      for (const dependency of operation.dependsOn) {
        if (!knownOperationIds.has(dependency)) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['operations', index, 'dependsOn'],
            message: `Unknown operation dependency: ${dependency}`,
          });
        }
      }
    }
    if (hasDependencyCycle(envelope.operations)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['operations'],
        message: 'Operation dependencies cannot form a cycle',
      });
    }
    const selectionOperations = envelope.operations.filter(
      (operation) => operation.selection !== undefined,
    );
    if (selectionOperations.length > 0) {
      const reference = selectionOperations[0];
      if (
        reference !== undefined &&
        !selectionOperations.every(
          (operation) =>
            operation.entity === reference.entity && operation.operation === reference.operation,
        )
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['operations'],
          message: 'selection operations must be homogeneous: same entity and operation',
        });
      }
    }
    for (const [index, operation] of envelope.operations.entries()) {
      const filter = operation.selection?.filter;
      if (filter === undefined) continue;
      const violation = findBulkFilterLimitViolation(filter);
      if (violation !== undefined) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['operations', index, 'selection', 'filter'],
          message:
            violation === 'depth'
              ? 'selection filter exceeds max depth 4'
              : 'selection filter exceeds max node count 32',
        });
      }
    }
  });

export type SourceFact = z.infer<typeof sourceFactSchema>;
export type SourceField = z.infer<typeof sourceFieldSchema>;
export type EntityReferenceDraft = z.infer<typeof entityReferenceDraftSchema>;
export type RelationEntityReferenceDraft = z.infer<typeof relationEntityReferenceDraftSchema>;
export type RelationDraft = z.infer<typeof relationDraftSchema>;
export type MissingInformation = z.infer<typeof missingInformationSchema>;
export type IntentOperation = z.infer<typeof intentOperationSchema>;
export type IntentEnvelopeV1 = z.infer<typeof intentEnvelopeV1Schema>;

export function sourceFieldItems(value: SourceField['rawValue']): readonly string[] {
  return Array.isArray(value) ? value : [value];
}

export function isTrustedContextEntityReference(
  reference:
    | EntityReferenceDraft
    | RelationEntityReferenceDraft
    | NonNullable<IntentEnvelopeV1['specialTarget']>,
): reference is Extract<
  EntityReferenceDraft | RelationEntityReferenceDraft,
  { readonly source: 'trusted_context' }
> {
  return reference.source === 'trusted_context';
}

export function parseIntentEnvelopeV1(input: unknown): IntentEnvelopeV1 {
  return intentEnvelopeV1Schema.parse(input);
}
